/**
 * LanguageSelector Component
 * Independent language selector for live session speech recognition.
 * This does NOT change the website language — it only controls which language
 * the speech recogniser uses during the live avatar session.
 *
 * Mirrors the mobile app's ChatInputBar language button behavior.
 */

import type React from 'react';
import { useTranslation } from 'react-i18next';
import { CoachMark } from '../CoachMark';
import './LanguageSelector.css';

/** STT language definition with labels and full names */
export interface STTLanguage {
  code: string;
  shortCode: string;
  label: string;
  name: string;
  nativeName: string;
}

/** STT language options — ordered: English first, then popular (Turkish, Arabic), then rest */
export const STT_LANGUAGES: STTLanguage[] = [
  { code: 'en-US', shortCode: 'en', label: 'EN', name: 'English', nativeName: 'English' },
  { code: 'tr-TR', shortCode: 'tr', label: 'TR', name: 'Turkish', nativeName: 'Türkçe' },
  { code: 'ar-SA', shortCode: 'ar', label: 'AR', name: 'Arabic', nativeName: 'العربية' },
  { code: 'es-ES', shortCode: 'es', label: 'ES', name: 'Spanish', nativeName: 'Español' },
  { code: 'fr-FR', shortCode: 'fr', label: 'FR', name: 'French', nativeName: 'Français' },
  { code: 'de-DE', shortCode: 'de', label: 'DE', name: 'German', nativeName: 'Deutsch' },
  { code: 'it-IT', shortCode: 'it', label: 'IT', name: 'Italian', nativeName: 'Italiano' },
  { code: 'pt-PT', shortCode: 'pt', label: 'PT', name: 'Portuguese', nativeName: 'Português' },
  { code: 'ru-RU', shortCode: 'ru', label: 'RU', name: 'Russian', nativeName: 'Русский' },
  { code: 'zh-CN', shortCode: 'zh', label: 'ZH', name: 'Chinese', nativeName: '中文' },
  { code: 'ja-JP', shortCode: 'ja', label: 'JA', name: 'Japanese', nativeName: '日本語' },
  { code: 'ko-KR', shortCode: 'ko', label: 'KO', name: 'Korean', nativeName: '한국어' },
  { code: 'ur-PK', shortCode: 'ur', label: 'UR', name: 'Urdu', nativeName: 'اردو' },
  {
    code: 'id-ID',
    shortCode: 'id',
    label: 'ID',
    name: 'Indonesian',
    nativeName: 'Bahasa Indonesia',
  },
  { code: 'fa-IR', shortCode: 'fa', label: 'FA', name: 'Persian', nativeName: 'فارسی' },
  { code: 'hi-IN', shortCode: 'hi', label: 'HI', name: 'Hindi', nativeName: 'हिन्दी' },
  { code: 'bn-BD', shortCode: 'bn', label: 'BN', name: 'Bengali', nativeName: 'বাংলা' },
];

/**
 * Resolves language details (full English name, short 2-letter code, etc.)
 * from a BCP-47 tag (e.g. 'tr-TR', 'en-US') or short code ('tr', 'en').
 */
export const getLanguageInfo = (codeOrShortCode: string): STTLanguage => {
  const normalized = (codeOrShortCode || 'en').toLowerCase();
  const short = normalized.split('-')[0];
  const found = STT_LANGUAGES.find(
    (l) => l.code.toLowerCase() === normalized || l.shortCode.toLowerCase() === short,
  );
  if (found) return found;
  return {
    code: codeOrShortCode,
    shortCode: short,
    label: short.toUpperCase(),
    name: short === 'tr' ? 'Turkish' : short === 'ar' ? 'Arabic' : 'English',
    nativeName: short === 'tr' ? 'Türkçe' : short === 'ar' ? 'العربية' : 'English',
  };
};

interface LanguageSelectorProps {
  /** Currently selected BCP-47 language code */
  value: string;
  /** Called when the user picks a new language */
  onChange: (languageCode: string) => void;
  className?: string;
  variant?: 'mobile' | 'desktop';
  disabled?: boolean;
}

export const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  value,
  onChange,
  className = '',
  variant = 'desktop',
  disabled = false,
}) => {
  const { t } = useTranslation();

  return (
    <CoachMark
      id="stt-language"
      message={t(
        'chat.languageCoachMark',
        'Select your preferred language before starting a live session.',
      )}
      buttonText={t('chat.gotIt', 'Got it')}
      position="top"
    >
      <div
        className={`language-selector-wrapper ${variant} ${disabled ? 'disabled' : ''} ${className}`}
      >
        <svg
          className="globe-icon"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          role="img"
          aria-label="Globe icon"
        >
          <title>Globe icon</title>
          <circle cx="12" cy="12" r="10" />
          <line x1="2" y1="12" x2="22" y2="12" />
          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
        </svg>
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="language-selector-select"
          title="Speech Language"
          id="stt-language-selector"
          disabled={disabled}
        >
          {STT_LANGUAGES.map((lang) => (
            <option key={lang.code} value={lang.code}>
              {lang.label} ({lang.nativeName})
            </option>
          ))}
        </select>
      </div>
    </CoachMark>
  );
};
